import type { WindowGeneratorPayload } from '../../../services/windowGeneratorTemplates.service';
import { mergeModalStateIntoPayload } from '../components/QuoteVisualDesignerCard';
import { resolveCanonicalTemplatePayload } from './windowPayloadPatches';
import { parseSemanticTricklePosition } from './trickleVentPosition';
import { isDoorOnlyTypeId } from './doorTypes';

type VisualDesignerMetaLike = {
  designer_version?: string;
  window_type?: { id?: string } | null;
  window_style?: Record<string, unknown>;
  specs?: Record<string, unknown>;
};

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toStr(value: unknown): string {
  return value == null ? '' : String(value);
}

function readSavedTemplatePayload(meta: VisualDesignerMetaLike): WindowGeneratorPayload | null {
  const ws = (meta.window_style || {}) as Record<string, unknown>;
  const payload =
    (ws.templatePayload as WindowGeneratorPayload | undefined) ||
    (ws.template_payload as WindowGeneratorPayload | undefined);
  if (!payload?.root || payload.w == null || payload.h == null) return null;
  return payload;
}

/**
 * v2_lite Window Generator lines that persisted an accepted per-line templatePayload on save.
 * Library-only references (no saved templatePayload on the line) remain legacy.
 */
export function hasAuthoritativeSavedTemplatePayload(meta: VisualDesignerMetaLike): boolean {
  if (String(meta?.designer_version || '').trim() !== 'v2_lite') return false;
  const ws = (meta.window_style || {}) as Record<string, unknown>;
  if (String(ws.source || '').trim().toLowerCase() !== 'window_generator') return false;
  return !!readSavedTemplatePayload(meta);
}

/** Legacy compact-card merge: overlay saved specs onto payload but preserve payload pixel geometry. */
function buildLegacyQuoteLineCardMergeState(
  meta: VisualDesignerMetaLike,
  templatePayload: WindowGeneratorPayload
): NonNullable<Parameters<typeof mergeModalStateIntoPayload>[1]> {
  const specs = (meta.specs || {}) as Record<string, unknown>;
  const opts = ((specs.options || {}) as Record<string, unknown>);
  const existingPres: Record<string, unknown> = ((templatePayload as any)?.presentation || {}) as Record<string, unknown>;
  const extHexForCard = toStr(opts.frame_color_hex_external || existingPres.frameColExt || '#F6F6F6');
  const intHexForCard = toStr(opts.frame_color_hex_internal || existingPres.frameColInt || '#F6F6F6');
  const isDoorMeta = isDoorOnlyTypeId(meta?.window_type?.id);
  const styleCategory = String((meta.window_style as Record<string, unknown> | undefined)?.category || '').toLowerCase();

  return {
    frameColorCode: toStr(specs.frame_color_code || 'white-smooth'),
    frameColorExtCode: toStr(specs.frame_color_code_external || ''),
    frameColorIntCode: toStr(specs.frame_color_code_internal || ''),
    frameColorExtName: toStr(specs.frame_color_external || ''),
    frameColorIntName: toStr(specs.frame_color_internal || ''),
    frameColorExtHex: extHexForCard,
    frameColorIntHex: intHexForCard,
    preferExplicitFrameValues: true,
    designDimsOn: opts.design_dims_on === true,
    glassTypeCode: String(specs.glass_type_code || '').toLowerCase() === 'obscured' ? 'obscured' : 'clear',
    widthMm: '',
    heightMm: '',
    handleColorCode: toStr(opts.handle_color_code || ''),
    handleStyleCode: toStr(opts.handle_style_code || (isDoorMeta ? 'lever' : 'offset')),
    trickleVents: opts.trickle_vents === true ? 'yes' : 'no',
    trickleVentsPosition: parseSemanticTricklePosition(String(opts.trickle_vents_position ?? '')),
    cillOption: (opts.cill_option as 'none' | '150' | '180' | 'stub') || 'none',
    extTopMm: toStr(opts.ext_top_mm ?? ''),
    extBottomMm: toStr(opts.ext_bottom_mm ?? ''),
    extLeftMm: toStr(opts.ext_left_mm ?? ''),
    extRightMm: toStr(opts.ext_right_mm ?? ''),
    georgianOn: opts.georgian_bars === true ? 'yes' : opts.georgian_bars === false ? 'no' : undefined,
    georgianCols: toStr(opts.georgian_cols ?? ''),
    georgianRows: toStr(opts.georgian_rows ?? ''),
    georgianWidthMm: toStr(opts.georgian_width_mm ?? ''),
    georgianOffXPct: toStr(opts.georgian_offset_x_pct ?? ''),
    georgianOffYPct: toStr(opts.georgian_offset_y_pct ?? ''),
    glassStyleTilePath: toStr(opts.glass_style_tile ?? ''),
    astragalOn: opts.astragal_bars === true ? 'yes' : opts.astragal_bars === false ? 'no' : undefined,
    astragalCols: toStr(opts.astragal_cols ?? ''),
    astragalRows: toStr(opts.astragal_rows ?? ''),
    astragalWidthMm: toStr(opts.astragal_width_mm ?? ''),
    astragalOffXPct: toStr(opts.astragal_offset_x_pct ?? ''),
    astragalOffYPct: toStr(opts.astragal_offset_y_pct ?? ''),
    logicalDivsBackup: (opts.logical_divs && typeof opts.logical_divs === 'object') ? (opts.logical_divs as Record<string, unknown>) : null,
    forceNoOpenings: styleCategory === 'fixed',
    isDoor: isDoorMeta,
  };
}

/** Legacy export/PDF merge: reconcile specs (including dimensions) onto templatePayload. */
function buildLegacyExportMergeState(meta: VisualDesignerMetaLike): NonNullable<Parameters<typeof mergeModalStateIntoPayload>[1]> {
  const specs = (meta.specs || {}) as Record<string, unknown>;
  const opts = ((specs.options || {}) as Record<string, unknown>);
  const bayTangentWidthMm = Number(opts.bay_tangent_width_mm ?? 0) || 0;
  const isDoorMeta = isDoorOnlyTypeId(meta?.window_type?.id);
  const styleCategory = String((meta.window_style as Record<string, unknown> | undefined)?.category || '').toLowerCase();

  return {
    frameColorCode: toStr(specs.frame_color_code || ''),
    frameColorExtCode: toStr(specs.frame_color_code_external || ''),
    frameColorIntCode: toStr(specs.frame_color_code_internal || ''),
    frameColorExtName: toStr(specs.frame_color_external || ''),
    frameColorIntName: toStr(specs.frame_color_internal || ''),
    frameColorExtHex: toStr(opts.frame_color_hex_external || ''),
    frameColorIntHex: toStr(opts.frame_color_hex_internal || ''),
    preferExplicitFrameValues: true,
    designDimsOn: opts.design_dims_on === true,
    glassTypeCode: String(specs.glass_type_code || '').toLowerCase() === 'obscured' ? 'obscured' : 'clear',
    widthMm: toStr((bayTangentWidthMm > 0 ? bayTangentWidthMm : specs.width_mm) ?? ''),
    heightMm: toStr(specs.height_mm ?? ''),
    handleColorCode: toStr(opts.handle_color_code || ''),
    handleStyleCode: toStr(opts.handle_style_code || (isDoorMeta ? 'lever' : 'inline')),
    trickleVents: opts.trickle_vents === true ? 'yes' : 'no',
    trickleVentsPosition: parseSemanticTricklePosition(String(opts.trickle_vents_position ?? '')),
    cillOption: (opts.cill_option as 'none' | '150' | '180' | 'stub') || 'none',
    extTopMm: toStr(opts.ext_top_mm ?? ''),
    extBottomMm: toStr(opts.ext_bottom_mm ?? ''),
    extLeftMm: toStr(opts.ext_left_mm ?? ''),
    extRightMm: toStr(opts.ext_right_mm ?? ''),
    georgianOn: opts.georgian_bars === true ? 'yes' : opts.georgian_bars === false ? 'no' : undefined,
    georgianCols: toStr(opts.georgian_cols ?? ''),
    georgianRows: toStr(opts.georgian_rows ?? ''),
    georgianWidthMm: toStr(opts.georgian_width_mm ?? ''),
    georgianOffXPct: toStr(opts.georgian_offset_x_pct ?? ''),
    georgianOffYPct: toStr(opts.georgian_offset_y_pct ?? ''),
    glassStyleTilePath: toStr(opts.glass_style_tile ?? ''),
    astragalOn: opts.astragal_bars === true ? 'yes' : opts.astragal_bars === false ? 'no' : undefined,
    astragalCols: toStr(opts.astragal_cols ?? ''),
    astragalRows: toStr(opts.astragal_rows ?? ''),
    astragalWidthMm: toStr(opts.astragal_width_mm ?? ''),
    astragalOffXPct: toStr(opts.astragal_offset_x_pct ?? ''),
    astragalOffYPct: toStr(opts.astragal_offset_y_pct ?? ''),
    logicalDivsBackup: (opts.logical_divs && typeof opts.logical_divs === 'object') ? (opts.logical_divs as Record<string, unknown>) : null,
    jointStyle: (opts.joint_style as 'mitre-45' | 'timber-weld') || 'mitre-45',
    isDoor: isDoorMeta,
    forceNoOpenings: styleCategory === 'fixed',
  };
}

export function resolveQuoteLineCardLivePayload(
  meta: VisualDesignerMetaLike,
  libraryTemplates: Array<{ id?: unknown; payload?: WindowGeneratorPayload }> = []
): WindowGeneratorPayload | null {
  if (hasAuthoritativeSavedTemplatePayload(meta)) {
    return cloneJson(readSavedTemplatePayload(meta)!);
  }

  const base = resolveCanonicalTemplatePayload(meta, libraryTemplates);
  if (!base) return null;

  try {
    const merged = mergeModalStateIntoPayload(base, buildLegacyQuoteLineCardMergeState(meta, base));
    return merged || base;
  } catch {
    return base;
  }
}

export function resolveExportWindowGeneratorPayload(meta: VisualDesignerMetaLike): WindowGeneratorPayload | null {
  const saved = readSavedTemplatePayload(meta);
  if (hasAuthoritativeSavedTemplatePayload(meta) && saved) {
    return cloneJson(saved);
  }

  const style = (meta.window_style || {}) as Record<string, unknown>;
  const templatePayload =
    (style.templatePayload as WindowGeneratorPayload | undefined) ||
    (style.template_payload as WindowGeneratorPayload | undefined) ||
    null;

  if (!templatePayload?.root || templatePayload.w == null || templatePayload.h == null) {
    return templatePayload;
  }

  try {
    const merged = mergeModalStateIntoPayload(templatePayload, buildLegacyExportMergeState(meta));
    return merged || templatePayload;
  } catch {
    return templatePayload;
  }
}
